import { AuditTimelineAction } from "../enums";
import { endpointsMetadata } from "../infrastructure/routes/endpointsMetadata";
import { IHistoryConfig } from "../components/history/models/historyModels";

function formatUtcToLocal(isoUtc: string, timezone?: string): string {
  const utcIso =
    isoUtc.endsWith("Z") || isoUtc.includes("+") ? isoUtc : isoUtc + "Z";
  const d = new Date(utcIso);
  return d.toLocaleString("bg-BG", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * " Иван Иванов (#123)" for the merge entries, or as much of it as the payload has. The
 * timeline renders entries as HTML and the name is free text, so it is escaped.
 */
function formatAccountReference(name: unknown, userId: unknown): string {
  const safeName =
    typeof name === "string" && name.trim() ? escapeHtml(name.trim()) : "";
  const idText = userId != null && userId !== "" ? `#${userId}` : "";

  if (safeName && idText) return ` ${safeName} (${idText})`;
  if (safeName) return ` ${safeName}`;
  if (idText) return ` ${idText}`;
  return "";
}

// i18n-ready: locale param is scaffolded for future translation support.
// Currently all text is in Bulgarian.
export function generateUserAuditText(
  action: number,
  payload: Record<string, any> | null | undefined,
  actorDisplayName: string | null | undefined,
  timezone?: string,
  _locale = "bg",
): string {
  const actor = actorDisplayName ?? "Системата";
  const p = payload ?? {};

  switch (action as AuditTimelineAction) {
    case AuditTimelineAction.UserRegistered:
      return actorDisplayName
        ? `${actor} регистрира акаунта`
        : "Акаунтът е създаден от потребителя (непотвърден)";

    case AuditTimelineAction.UserCardRefilled:
      return `${actor} зареди абонамент „${p.planName ?? ""}" — ${p.visits ?? ""} посещения за € ${p.price ?? ""}`;

    case AuditTimelineAction.UserCardEdited:
      return `${actor} редактира абонамент „${p.planName ?? ""}"`;

    case AuditTimelineAction.UserCardRemoved:
      return `${actor} премахна абонамент „${p.planName ?? ""}"`;

    case AuditTimelineAction.UserCardRestored:
      return `${actor} възстанови абонамент „${p.planName ?? ""}"`;

    case AuditTimelineAction.UserProfileEdited:
      return `${actor} редактира профила`;

    case AuditTimelineAction.UserDeleted:
      return `${actor} изтри акаунта`;

    case AuditTimelineAction.UserRestored:
      return `${actor} възстанови акаунта`;

    case AuditTimelineAction.UserPasswordResetLinkGenerated:
      return `${actor} генерира линк за задаване на парола`;

    case AuditTimelineAction.UserReliabilityToggled:
      if (p.isReliable) return `${actor} отбеляза клиента като редовен`;
      if (p.reason === "PenaltyCancelled")
        return `${actor} маркира клиента като нередовен (поради отказана резервация с наказание)`;
      return `${actor} отбеляза клиента като нередовен`;

    case AuditTimelineAction.UserNoteAdded:
      return `${actor} добави бележка${p.notePreview ? `: „${p.notePreview}"` : ""}`;

    case AuditTimelineAction.UserNoteEdited:
      return `${actor} редактира бележка${p.notePreview ? `: „${p.notePreview}"` : ""}`;

    case AuditTimelineAction.UserNoteDeleted:
      return `${actor} изтри бележка`;

    case AuditTimelineAction.UserFamilyAssigned:
      return `${actor} добави към семейство „${p.familyName ?? ""}"`;

    case AuditTimelineAction.UserFamilyRemoved:
      return `${actor} премахна от семейство „${p.familyName ?? ""}"`;

    case AuditTimelineAction.VerificationCodeSent:
      return "Изпратен код за потвърждение";

    case AuditTimelineAction.UserAccountLocked: {
      const lockoutText = p.lockoutEnd
        ? ` до ${formatUtcToLocal(p.lockoutEnd, timezone)}`
        : "";
      return `Акаунтът е заключен временно поради множество неуспешни опити${lockoutText}`;
    }

    case AuditTimelineAction.UserEmailConfirmed:
      return actorDisplayName
        ? `${actor} потвърди ръчно имейл адреса на потребителя`
        : "Потребителят потвърди имейл адреса си чрез код";

    case AuditTimelineAction.UserFirstLogin:
      return "Първо влизане в системата";

    case AuditTimelineAction.UserLoggedIn:
      return "Влизане в системата";

    case AuditTimelineAction.EmployeeWorkingHoursUpdated:
      return `${actor} промени работния график`;

    case AuditTimelineAction.UserPasswordChanged:
      return `${actor} смени паролата си`;

    case AuditTimelineAction.UserOrderCreated:
      return `${actor} създаде поръчка ${p.displayNumber ?? ""}`;

    case AuditTimelineAction.UserRfidBandAssigned:
      return p.previousRfidBandId
        ? `${actor} смени RFID картата (${p.previousRfidBandId} → ${p.rfidBandId ?? ""})`
        : `${actor} присвои RFID карта ${p.rfidBandId ?? ""}`;

    case AuditTimelineAction.UserRfidBandRemoved:
      return `${actor} премахна RFID картата${p.previousRfidBandId ? ` (${p.previousRfidBandId})` : ""}`;

    case AuditTimelineAction.UserReservationsBulkPermanentlyDeleted:
      return `${actor} перманентно изтри ${p.count ?? ""} резервации`;

    case AuditTimelineAction.UserReservationCreated: {
      const forClass = p.eventName
        ? ` за „${p.eventName}"${p.eventDate ? ` на ${p.eventDate}` : ""}`
        : "";
      const extra =
        p.eventCount > 1 ? ` и още ${p.eventCount - 1} други` : "";
      const who = p.selfBooked ? "Резервира си час" : `${actor} записа клиента`;

      if (p.isWaitlist)
        return `${who}${forClass}${extra} (списък на чакащите)`;

      return `${who}${forClass}${extra}`;
    }

    case AuditTimelineAction.UserReservationCancelled: {
      const forClass = p.eventName
        ? ` за „${p.eventName}"${p.eventDate ? ` на ${p.eventDate}` : ""}`
        : "";

      return p.selfCancelled
        ? `Отказа резервацията си${forClass}`
        : `${actor} отказа резервацията${forClass}`;
    }

    case AuditTimelineAction.UserGymCheckedIn: {
      if (p.planName) return `Влезе в залата (абонамент „${p.planName}")`;
      if (p.pricePaid != null)
        return `Влезе в залата (плати ${p.pricePaid} лв.)`;
      return "Влезе в залата";
    }

    case AuditTimelineAction.UserGymCheckedOut:
      return p.minutesInside != null
        ? `Излезе от залата (престой ${p.minutesInside} мин.)`
        : "Излезе от залата";

    case AuditTimelineAction.UserDepositToppedUp:
      return `${actor} зареди депозит с ${p.amount ?? ""} лв.${p.comment ? ` — ${p.comment}` : ""}`;

    case AuditTimelineAction.UserDepositWithdrawn:
      return `${actor} изтегли ${p.amount ?? ""} лв. от депозита${p.comment ? ` — ${p.comment}` : ""}`;

    case AuditTimelineAction.UserImpersonatedByAdmin:
      // The timeline renders entries as HTML, and the reason is free text.
      return `${actor} (Planny) влезе в профила от админ панела${p.reason ? `. Причина: „${escapeHtml(String(p.reason))}"` : ""}`;

    case AuditTimelineAction.UserAccountsMerged:
      return `Слят с акаунт${formatAccountReference(p.sourceName, p.sourceUserId)}`;

    case AuditTimelineAction.UserMergedIntoAnotherAccount:
      return `Слят в акаунт${formatAccountReference(p.targetName, p.targetUserId)}`;

    default:
      return "Неизвестно действие";
  }
}

export function getUserAuditActionCategory(
  action: number,
): "account" | "card" | "security" | "social" | "reliability" {
  const a = action as AuditTimelineAction;
  if (
    [
      AuditTimelineAction.UserRegistered,
      AuditTimelineAction.UserProfileEdited,
      AuditTimelineAction.UserDeleted,
      AuditTimelineAction.UserRestored,
      AuditTimelineAction.UserAccountsMerged,
      AuditTimelineAction.UserMergedIntoAnotherAccount,
      AuditTimelineAction.VerificationCodeSent,
      AuditTimelineAction.UserEmailConfirmed,
      AuditTimelineAction.UserFirstLogin,
      AuditTimelineAction.UserLoggedIn,
      AuditTimelineAction.EmployeeWorkingHoursUpdated,
    ].includes(a)
  )
    return "account";

  if (
    [
      AuditTimelineAction.UserCardRefilled,
      AuditTimelineAction.UserCardEdited,
      AuditTimelineAction.UserCardRemoved,
      AuditTimelineAction.UserCardRestored,
      AuditTimelineAction.UserOrderCreated,
      AuditTimelineAction.UserRfidBandAssigned,
      AuditTimelineAction.UserRfidBandRemoved,
      AuditTimelineAction.UserGymCheckedIn,
      AuditTimelineAction.UserGymCheckedOut,
      AuditTimelineAction.UserDepositToppedUp,
      AuditTimelineAction.UserDepositWithdrawn,
    ].includes(a)
  )
    return "card";

  if (
    [
      AuditTimelineAction.UserPasswordResetLinkGenerated,
      AuditTimelineAction.UserAccountLocked,
      AuditTimelineAction.UserPasswordChanged,
      AuditTimelineAction.UserReservationsBulkPermanentlyDeleted,
      AuditTimelineAction.UserImpersonatedByAdmin,
    ].includes(a)
  )
    return "security";

  if (
    [
      AuditTimelineAction.UserNoteAdded,
      AuditTimelineAction.UserNoteEdited,
      AuditTimelineAction.UserNoteDeleted,
      AuditTimelineAction.UserFamilyAssigned,
      AuditTimelineAction.UserFamilyRemoved,
    ].includes(a)
  )
    return "social";

  return "reliability";
}

export const AUDIT_CATEGORY_COLORS: Record<
  ReturnType<typeof getUserAuditActionCategory>,
  string
> = {
  account: "#6366f1",
  card: "#10b981",
  security: "#f59e0b",
  social: "#ec4899",
  reliability: "#eab308",
};

const dot = (color: string) =>
  `<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-right:8px;vertical-align:middle;flex-shrink:0"></span>`;

export const AUDIT_COLOR_LEGEND_HTML = `
<div style="line-height:2;min-width:180px">
  <div>${dot("#6366f1")}Акаунт &amp; Влизания</div>
  <div>${dot("#10b981")}Абонаменти, зала &amp; депозит</div>
  <div>${dot("#f59e0b")}Сигурност</div>
  <div>${dot("#ec4899")}Бележки &amp; Семейство</div>
  <div>${dot("#eab308")}Резервации</div>
</div>`.trim();

export const EMPLOYEE_AUDIT_COLOR_LEGEND_HTML = `
<div style="line-height:2;min-width:180px">
  <div>${dot("#6366f1")}Акаунт &amp; График</div>
  <div>${dot("#f59e0b")}Сигурност</div>
</div>`.trim();

export const HISTORY_CUTOFF_DATE = new Date("2026-03-30");

/**
 * Rows the user-history endpoint returns per page. Must match
 * IUserAuditLogService.HistoryPageSize on the backend, otherwise the dialog either
 * hides a "show more" it needs or offers one that returns nothing.
 */
export const USER_HISTORY_PAGE_SIZE = 100;

export const USER_HISTORY_CONFIG: IHistoryConfig = {
  historyEndpoint: endpointsMetadata.userAuditLog.getUserHistory,
  historyQueryParam: "userId",
  detailEndpoint: endpointsMetadata.userAuditLog.getDetail,
  cutoffDate: HISTORY_CUTOFF_DATE,
  cutoffDateLabel: "30.03.2026 г.",
  tooltipHtml: AUDIT_COLOR_LEGEND_HTML,
  generateText: generateUserAuditText,
  getCategory: getUserAuditActionCategory,
  categoryColors: AUDIT_CATEGORY_COLORS,
  pageSize: USER_HISTORY_PAGE_SIZE,
};

export const EMPLOYEE_HISTORY_CONFIG: IHistoryConfig = {
  historyEndpoint: endpointsMetadata.userAuditLog.getUserHistory,
  historyQueryParam: "userId",
  detailEndpoint: endpointsMetadata.userAuditLog.getDetail,
  cutoffDate: HISTORY_CUTOFF_DATE,
  cutoffDateLabel: "30.03.2026 г.",
  tooltipHtml: EMPLOYEE_AUDIT_COLOR_LEGEND_HTML,
  generateText: generateUserAuditText,
  getCategory: getUserAuditActionCategory,
  categoryColors: AUDIT_CATEGORY_COLORS,
  pageSize: USER_HISTORY_PAGE_SIZE,
};
